import { workspaceTextFor } from './interface-copy';
import type { CompanyResult, CompanySkill } from './types';
import { WorkspaceIcon } from './icons';
import type { ArtifactPanelsCopyKey } from '@/i18n/ns/artifact-panels';

/** The saved result's real kind as the Library shows it: the legacy result kinds, plus a saved App. */
export type ResultDisplayKind = CompanyResult['kind'] | 'app';

/** The existing artifact-panels kind label (24 languages) for each result kind shown in the Library. */
export const RESULT_KIND_COPY_KEY = {
  markdown: 'kindDocument',
  table: 'kindTable',
  chart: 'kindChart',
  deck: 'kindDeck',
  html: 'kindPage',
  app: 'kindApp',
} as const satisfies Record<ResultDisplayKind, ArtifactPanelsCopyKey>;

/** What made this result: nothing (ad-hoc), a published Skill run, a preview of
 * an unpublished draft, a Skill this workspace no longer has, or a Skill we
 * cannot name yet because the workspace is still loading or could not be read. */
export interface ResultLineage {
  state: 'ad-hoc' | 'skill-run' | 'draft-preview' | 'unavailable' | 'pending' | 'unread';
  skill: CompanySkill | null;
  name: string;
  version: number;
}
/** How the read of the company's Skills went. One value rather than a pair of
 * booleans: only a read that finished successfully can establish absence. */
export type WorkspaceRead = 'pending' | 'read' | 'failed';

/** When this result last moved, or null if it never has.
 *
 * A revision through `replaces` keeps the result's id, so a citation made
 * before it still lands on the same canvas — showing different content. The
 * date is how a reader who followed that link can tell.
 *
 * Both columns default to the same `now()` on insert and migration 0020
 * backfills `updated_at = created_at`, so an untouched result compares equal
 * and claims nothing. `tool_projections.resultReceipt` makes the same call for
 * the model; the two are held together by their own tests. */
export function revisionDate(
  result: Pick<CompanyResult, 'createdAt' | 'updatedAt'>,
): string | null {
  return result.updatedAt && result.updatedAt !== result.createdAt ? result.updatedAt : null;
}

export function workspaceRead(loading: boolean, error: string | null): WorkspaceRead {
  if (loading) return 'pending';
  return error ? 'failed' : 'read';
}

const adHoc: ResultLineage = { state: 'ad-hoc', skill: null, name: '', version: 0 };
const unavailable: ResultLineage = { state: 'unavailable', skill: null, name: '', version: 0 };
const pending: ResultLineage = { state: 'pending', skill: null, name: '', version: 0 };
const unread: ResultLineage = { state: 'unread', skill: null, name: '', version: 0 };

/** A Skill the workspace does not hold is missing only if the workspace was
 * actually read: still loading is unknown, and a failed read is unknown too. */
function unresolved(read: WorkspaceRead): ResultLineage {
  if (read === 'pending') return pending;
  return read === 'failed' ? unread : unavailable;
}

export function resultLineage(
  result: CompanyResult,
  skills: CompanySkill[],
  read: WorkspaceRead,
): ResultLineage {
  // A run the workspace cannot resolve is still evidence the work came from a
  // Skill: never offer to save an approach that is already one.
  if (!result.skillId) return result.runId ? unresolved(read) : adHoc;
  // A Skill already held is resolved whatever the last read did, so a failed
  // refresh never costs a result its Skill.
  const skill = skills.find((item) => item.id === result.skillId);
  if (!skill) return unresolved(read);
  const preview = result.runMode === 'preview';
  const known = preview ? null : skill.publishedRevision;
  return {
    state: preview ? 'draft-preview' : 'skill-run',
    skill,
    // Skills are named by their working draft everywhere else in the workspace.
    name: skill.draft.name,
    version: result.skillRevision ?? known ?? skill.currentRevision,
  };
}

/** The compact Library-row line under a result title. */
export function lineageLabel(lineage: ResultLineage, locale = 'en'): string {
  const text = workspaceTextFor(locale);
  if (lineage.state === 'ad-hoc') return text('Saved result');
  if (!lineage.skill) return text('Made with a Skill');
  return text(lineage.state === 'draft-preview' ? 'Preview of {name}' : 'Made with {name}', {
    name: lineage.name,
  });
}

/** The Run row in the result's Details tab. */
export function lineageDetail(lineage: ResultLineage, locale = 'en'): string {
  const text = workspaceTextFor(locale);
  if (lineage.state === 'ad-hoc') return text('Ad-hoc result');
  if (lineage.state === 'pending' || lineage.state === 'unread') return text('Made with a Skill');
  if (lineage.state === 'unavailable')
    return text('A Skill that is not available in this workspace');
  return text(
    lineage.state === 'draft-preview'
      ? '{name} · Revision {version}'
      : '{name} · Version {version}',
    { name: lineage.name, version: lineage.version },
  );
}

/** Format artwork, not a preview of the saved result's values. */
export function ResultThumbnail({ kind }: { kind: ResultDisplayKind }) {
  if (kind === 'app')
    // A saved App has no format artwork: the same tile with the App icon.
    return (
      <span className="ws-result-thumbnail ws-result-thumbnail-app" aria-hidden="true">
        <WorkspaceIcon name="app" size={28} />
      </span>
    );
  return (
    <span className={`ws-result-thumbnail ws-result-thumbnail-${kind}`} aria-hidden="true">
      <svg
        viewBox="0 0 88 56"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {kind === 'html' ? (
          <>
            <rect x="12" y="7" width="64" height="42" rx="3" fill="var(--ws-thumbnail-paper)" />
            <path d="M12 16h64" opacity=".65" />
            <path d="M18 11.5h.01M23 11.5h.01M28 11.5h.01" strokeWidth="2.5" />
            <rect x="19" y="22" width="22" height="20" rx="1.5" opacity=".5" />
            <path d="M47 24h22M47 31h22M47 38h14" opacity=".65" />
          </>
        ) : kind === 'deck' ? (
          <>
            <rect x="9" y="7" width="52" height="33" rx="2" fill="var(--ws-thumbnail-paper)" />
            <path d="M15 15h22M15 22h30M15 29h18" opacity=".65" />
            <rect x="65" y="14" width="14" height="10" rx="1.5" opacity=".5" />
            <rect x="65" y="28" width="14" height="10" rx="1.5" opacity=".5" />
            <path d="M31 46h26" opacity=".5" />
          </>
        ) : kind === 'markdown' ? (
          <>
            <path d="M28 5h24l10 10v36H28Z" fill="var(--ws-thumbnail-paper)" />
            <path d="M52 5v10h10" />
            <path d="M35 23h20M35 29h20M35 35h15M35 41h18" opacity=".65" />
            <path d="M35 16h9" strokeWidth="2.5" />
          </>
        ) : kind === 'table' ? (
          <>
            <rect x="13" y="9" width="62" height="39" rx="3" fill="var(--ws-thumbnail-paper)" />
            <path d="M14 19h60M14 29h60M14 39h60M34 19v28M55 19v28" opacity=".65" />
            <path d="M20 14h12M41 14h7M62 14h6" strokeWidth="2" />
            <path d="M20 24h7M41 34h7M62 44h6" opacity=".6" />
          </>
        ) : (
          <>
            <path d="M13 47h64" opacity=".35" />
            <rect
              x="17"
              y="28"
              width="11"
              height="18"
              rx="1.5"
              fill="currentColor"
              stroke="none"
              opacity=".5"
            />
            <rect x="33" y="10" width="11" height="36" rx="1.5" fill="currentColor" stroke="none" />
            <rect
              x="49"
              y="23"
              width="11"
              height="23"
              rx="1.5"
              fill="currentColor"
              stroke="none"
              opacity=".65"
            />
            <rect
              x="65"
              y="17"
              width="11"
              height="29"
              rx="1.5"
              fill="currentColor"
              stroke="none"
              opacity=".3"
            />
          </>
        )}
      </svg>
    </span>
  );
}
